import { Fetch } from "./util/InterceptedFetch";
import { BuildUri } from "./util/BuildUri";

import {
    CreateHorseForSaleRequest,
    HorseForSaleApprovalRequest,
    OutcomeRequest,
    HorseForSalePurchaseRequest,
    StableReleaseRequest,
    WithdrawalRequest
} from "../requests/HorseForSaleRequests";
import { MultiPatchRequest } from "../requests/PatchRequests";

import { ListWrapper } from "../model/ListWrapper";
import { HorseForSale, GeocodedHorseForSale } from "../model/HorseForSale";

export interface HorseForSaleFilters {
    name?: string;
    states?: string;
    saleIds?: string;
    gaitIds?: string;
    consignorIds?: string;
    fullTextSearch?: string;
}

export class HorseForSaleEndpoints {
    constructor(private fetch: Fetch) {}

    getHorseForSaleList(
        limit: number = 10,
        startAt: number = 0,
        orderBy: string | undefined = undefined,
        filters: HorseForSaleFilters = {}
    ): Promise<ListWrapper<HorseForSale>> {
        return this.fetch(BuildUri("/horse-for-sale", {}, { limit, startAt, orderBy, ...filters }));
    }

    createHorseForSale(request: CreateHorseForSaleRequest): Promise<HorseForSale> {
        return this.fetch(BuildUri("/horse-for-sale"), { method: "POST", body: request });
    }

    approveOrRejectEntryHorseForSale(horseForSaleId: number, request: HorseForSaleApprovalRequest): Promise<HorseForSale> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}/approval", { horseForSaleId }), { method: "POST", body: request });
    }

    getHorseForSale(horseForSaleId: number): Promise<HorseForSale> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}", { horseForSaleId }));
    }

    editHorseForSale(horseForSaleId: number, patchRequests: MultiPatchRequest): Promise<HorseForSale> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}", { horseForSaleId }), { method: "PATCH", body: patchRequests });
    }

    deleteHorseForSale(horseForSaleId: number): Promise<void> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}", { horseForSaleId }), { method: "DELETE" });
    }

    getInspectionForms(saleId: number, date: string): Promise<Blob> {
        return this.fetch(BuildUri("/horse-for-sale/inspection-forms/{saleId}?for-date={date}", { saleId, date }), { returnBlob: true });
    }

    generateAcknowledgementOfPurchaseDocument(saleId: number): Promise<Blob> {
        return this.fetch(BuildUri("/horse-for-sale/acknowledgement-of-purchase/{saleId}", { saleId }), { returnBlob: true });
    }

    generateStableReleaseDocument(saleId: number): Promise<Blob> {
        return this.fetch(BuildUri("/horse-for-sale/stable-release/{saleId}", { saleId }), { returnBlob: true });
    }

    getHorseForSaleCoordinatesList(saleId: number): Promise<ListWrapper<GeocodedHorseForSale>> {
        return this.fetch(BuildUri("/horse-for-sale/coords/{saleId}", { saleId }));
    }

    generateHipNumbers(saleId: number): Promise<void> {
        return this.fetch(BuildUri("/horse-for-sale/hip-numbers/{saleId}", { saleId }), { method: "POST" });
    }

    swapHipNumbers(request: { [field: string]: number }): Promise<void> {
        return this.fetch(BuildUri("/horse-for-sale/hip-numbers"), { method: "PATCH", body: request });
    }

    generateCogginsCertificateFeeInvoice(horseForSaleId: number): Promise<void> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}/invoice/coggins", { horseForSaleId }), { method: "POST" });
    }

    generateRegistrationCertificateFeeInvoice(horseForSaleId: number): Promise<void> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}/invoice/registration", { horseForSaleId }), { method: "POST" });
    }

    registerOutcome(horseForSaleId: number, request: OutcomeRequest): Promise<HorseForSale> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}/outcome", { horseForSaleId }), { method: "POST", body: request });
    }

    updateOutcome(horseForSaleId: number, request: OutcomeRequest): Promise<HorseForSale> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}/outcome", { horseForSaleId }), { method: "PUT", body: request });
    }

    withdrawHorseForSaleEarly(horseForSaleId: number, request: WithdrawalRequest): Promise<HorseForSale> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}/withdraw-early", { horseForSaleId }), { method: "POST", body: request });
    }

    editEarlyWithdrawalNotes(horseForSaleId: number, request: WithdrawalRequest): Promise<HorseForSale> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}/withdraw-early", { horseForSaleId }), { method: "PUT", body: request });
    }

    generateEntryFeeInvoice(horseForSaleId: number): Promise<void> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}/invoice/entry", { horseForSaleId }), { method: "POST" });
    }

    generateWithdrawalPenaltyInvoice(horseForSaleId: number): Promise<void> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}/invoice/withdrawal-penalty", { horseForSaleId }), { method: "POST" });
    }

    purchaseHorse(horseForSaleId: number, request: HorseForSalePurchaseRequest): Promise<HorseForSale> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}/purchase", { horseForSaleId }), { method: "POST", body: request });
    }

    stableRelease(horseForSaleId: number, request: StableReleaseRequest): Promise<HorseForSale> {
        return this.fetch(BuildUri("/horse-for-sale/{horseForSaleId}/outcome/stable-release", { horseForSaleId }), {
            method: "PUT",
            body: request
        });
    }

    // NOTE: Technically, the endpoint below belongs in a separate FinanceEndpoint

    generateEntryFeeInvoicesForSale(saleId: number) {
        return this.fetch(BuildUri("/finance/sale/{saleId}/entry-fee", { saleId }), { method: "POST" });
    }
}



// WEBPACK FOOTER //
// ./src/endpoints/HorseForSaleEndpoints.ts